// Step 4/5 — guideline child and spousal support (CAXeng.md §2.2–§2.6, §3).
import { otherParty, type Case, type CceAllocation, type LowIncomeRange, type PartyIndex } from '@divorceos/schema';
import {
  ADJUSTED_NETS_MAX_ITER, ADJUSTED_NETS_TOLERANCE, CA_MINIMUM_WAGE, HARDSHIP_MAX_ITER, HARDSHIP_TOLERANCE, K_MULTIPLIERS,
  LOW_INCOME_THRESHOLD, LOW_INCOME_THRESHOLD_FALLBACK, SB343_FULL_TIME_HOURS_PER_YEAR, SB343_OPERATIVE, SS_FORMULA_BY_METHOD,
} from './constants.js';
import type { NCase } from './normalize.js';
import { netDisposable, type PartyNets } from './net.js';
import { NO_SUPPORT, TaxContext, type ScenarioSpec, type TaxOutcome } from './taxes.js';

// ------------------------------------------------------------------------------------------ sign helpers
/** Amount paid by `payor` as a p0-signed number (> 0: party 0 pays party 1). */
export const signedBy = (amount: number, payor: PartyIndex): number => (payor === 0 ? amount : -amount);
/** Payor of a p0-signed amount (null when zero). */
export const payorOf = (signed: number): PartyIndex | null => (Math.abs(signed) < 0.005 ? null : signed > 0 ? 0 : 1);
/** Amount of a p0-signed number paid by party p (0 when p receives). */
export const paidBy = (signed: number, p: PartyIndex): number => (p === 0 ? Math.max(signed, 0) : Math.max(-signed, 0));
/** Net received by p: + when p receives, − when p pays. */
export const receivedBy = (signed: number, p: PartyIndex): number => (p === 0 ? -signed : signed);

// ------------------------------------------------------------------------------------------ FC 4055 version
/** The FC 4055 guideline a computation uses once ProgramSettings.fc4055Version is resolved. */
export type Fc4055Resolved = 'PRE_SB343' | 'SB343';

/**
 * Resolves ProgramSettings.fc4055Version for a case:
 *  - absent or 'PRE_SB343' → 'PRE_SB343' (the original Xspouse engine, bit-for-bit);
 *  - 'SB343' → 'SB343' for any tax year;
 *  - 'AUTO' → 'SB343' for tax years ≥ 2025; for 2024, 'SB343' when `now` (default: today, local calendar date) is on
 *    or after 1 Sep 2024 (FC 4055(d) operative date), else 'PRE_SB343'; for 2023 and earlier, 'PRE_SB343'.
 * The engine resolves with EngineOptions.now (or today), so the UI should pass the same `now`.
 */
export function resolveFc4055Version(c: Pick<Case, 'taxYear' | 'settings'>, now: Date = new Date()): Fc4055Resolved {
  const v = c.settings.fc4055Version;
  if (v === 'SB343') return 'SB343';
  if (v !== 'AUTO') return 'PRE_SB343';
  if (c.taxYear >= 2025) return 'SB343';
  if (c.taxYear < SB343_OPERATIVE.year) return 'PRE_SB343';
  const y = now.getFullYear(), m = now.getMonth(), d = now.getDate();
  const onOrAfter = y > SB343_OPERATIVE.year || (y === SB343_OPERATIVE.year && (m > SB343_OPERATIVE.monthIndex || (m === SB343_OPERATIVE.monthIndex && d >= SB343_OPERATIVE.day)));
  return onOrAfter ? 'SB343' : 'PRE_SB343';
}

/**
 * Add-on allocation in effect: under SB 343, FC 4061 makes the net-income proration the default apportionment.
 * The schema has no "user chose this" flag, so the shipped default 'FIFTY_FIFTY' is read as "not chosen" and becomes
 * 'FC4061B' when the case resolves to SB 343; any other explicit choice (incl. CP deduction) is kept. Consequence:
 * a strict 50/50 split cannot be selected under SB 343 (enter it as a USER amount or use PRE_SB343).
 */
export function effectiveCceAllocation(allocation: CceAllocation, version: Fc4055Resolved): CceAllocation {
  return version === 'SB343' && allocation === 'FIFTY_FIFTY' ? 'FC4061B' : allocation;
}

// ------------------------------------------------------------------------------------------ §2.2 K factor
/** §2.2 fraction(TN) bands (FUN_10015a7d). TN = combined monthly net. */
export function kFraction(tn: number): number {
  if (tn < 0) return 0;
  if (tn < 801) return 0.20 + tn / 16000;
  if (tn <= 6666) return 0.25;
  if (tn <= 10000) return 0.10 + 1000 / tn;
  return 0.12 + 800 / tn;
}

/**
 * FC 4055(b)(3) as amended by SB 343 (operative 1 Sep 2024) fraction(TN):
 * 0–2,900: 0.165 + TN/82,857; 2,901–5,000: 0.131 + TN/42,149; 5,001–10,000: 0.25; 10,001–15,000: 0.10 + 1,499/TN;
 * over 15,000: 0.12 + 1,200/TN. The statute's bands are whole dollars; fractional TN between bands (e.g. 2,900.40)
 * belongs to the lower band (band edges taken as "≤ 2,900", "≤ 5,000", ...).
 */
export function kFractionSb343(tn: number): number {
  if (tn < 0) return 0;
  if (tn <= 2900) return 0.165 + tn / 82857;
  if (tn <= 5000) return 0.131 + tn / 42149;
  if (tn <= 10000) return 0.25;
  if (tn <= 15000) return 0.10 + 1499 / tn;
  return 0.12 + 1200 / tn;
}

/** §2.2 K = fraction(TN) × (H% ≥ 0.5 ? 2 − H% : 1 + H%) × mult[min(nkids,10)]; fraction per the FC 4055 version. */
export function kFactor(tn: number, hPercent: number, nkids: number, version: Fc4055Resolved = 'PRE_SB343'): number {
  const m = K_MULTIPLIERS[Math.min(Math.max(0, Math.round(nkids)), 10)];
  const f = version === 'SB343' ? kFractionSb343(tn) : kFraction(tn);
  return f * (hPercent >= 0.5 ? 2 - hPercent : 1 + hPercent) * m;
}

/** §2.2 formula 1: CS = K · (HN − TN · H%), relative to the higher earner (negative → lower earner pays). */
export function basicCs(k: number, hn: number, ln: number, hPercent: number, nkids: number): number {
  return nkids > 0 ? k * (hn - (hn + ln) * hPercent) : 0;
}

/** §2.1 H% = (ts[hi]·n[lo] + (1 − ts[lo])·n[hi]) / nkids (time the higher earner has the children). */
export function hPercent(nc: NCase, hi: PartyIndex): number {
  const lo = otherParty(hi);
  const nkids = nc.nkids;
  if (nkids < 1) return 0;
  const H = (nc.parties[hi].ts * nc.parties[lo].n + (1 - nc.parties[lo].ts) * nc.parties[hi].n) / nkids;
  return Math.max(0, Math.min(1, H));
}

// ------------------------------------------------------------------------------------------ §3 SS formulas
export interface SsFormulaArgs {
  hn: number; ln: number; k: number;
  /** K for the K·G terms (defaults to k). */
  kG?: number;
  /** Raw formula nets (DAT_10028314) of the higher / lower earner (G_hi, G_lo). */
  gHi: number; gLo: number;
  nkids: number;
  /** Basic CS relative to the higher earner (for the user formula's ADJUST option). */
  csHi: number;
}

/** §3 county formulas by numeric id (2..8). Result relative to the higher earner, before floor/multiplier. */
export function countySsFormula(id: number, a: SsFormulaArgs): number {
  const { hn, ln, k, gHi, gLo, nkids } = a;
  const kG = a.kG ?? k;
  const noKids = 0.40 * hn - 0.50 * ln;
  if (nkids <= 0) return id === 7 ? 0.35 * hn - 0.50 * ln : noKids;
  switch (id) {
    case 2: return (1 - k) * (0.40 * hn - 0.50 * ln);
    case 3: case 8: return (1 - k) * (0.35 * hn - 0.40 * ln);
    case 4: case 5: return (1 - k) * (0.35 * hn - 0.45 * ln);
    case 6: return 0.40 * (hn - kG * gHi) - 0.50 * (ln - kG * gLo);
    case 7: return 0.30 * (hn - kG * gHi) - 0.50 * (ln - kG * gLo);
    default: return (1 - k) * (0.35 * hn - 0.45 * ln);
  }
}

/** §3 user formula (id 1, FUN_10015f1d). */
export function userSsFormula(nc: NCase, a: SsFormulaArgs): number {
  const f = nc.c.userSupport.formula;
  const A = f.addPercentHighEarner / 100;
  const B = f.subtractPercentLowEarner / 100;
  let { hn, ln } = a;
  let r: number;
  switch (f.adjust) {
    case 'ACTUAL_CS':
      if (a.csHi > 0) hn -= a.csHi; else ln += a.csHi;
      r = Math.max(hn, 0) * A - Math.max(ln, 0) * B;
      break;
    case 'PERCENT_ALLOCATED':
      r = (a.nkids > 0 ? 1 - a.k : 1) * (hn * A - ln * B);
      break;
    case 'DOLLAR_ALLOCATED':
      r = 0.40 * (hn - (a.kG ?? a.k) * a.gHi) - 0.50 * (ln - (a.kG ?? a.k) * a.gLo); // fixed 40/50 (intent UNCERTAIN in the notes)
      break;
    default:
      r = Math.max(hn, 0) * A - Math.max(ln, 0) * B;
  }
  return f.multiplyBy !== 1 ? r * f.multiplyBy : r;
}

/** Guideline SS relative to the higher earner: floored at 0 and × ssMultiplier. USER is handled by the caller. */
export function formulaSs(nc: NCase, a: SsFormulaArgs): number {
  const id = SS_FORMULA_BY_METHOD[nc.c.settings.ssMethod];
  let r = id === 1 ? userSsFormula(nc, a) : countySsFormula(id, a);
  r = Math.max(0, r);
  const m = nc.c.settings.ssMultiplier;
  return m !== 1 ? r * m : r;
}

// ------------------------------------------------------------------------------------------ CS/SS from nets
export interface SupportFromNets {
  hi: PartyIndex;
  hPercent: number;
  k: number;
  /** p0-signed basic CS and SS. */
  cs: number;
  ss: number;
  /** CS relative to hi (signed). */
  csHi: number;
}

export interface FormulaOptions {
  /** Tactic-9 mode (FUN_100152cc): per-child user amounts are substituted when the CS method is USER (§4) and the low-income adjustment is not applied. */
  tacticMode?: boolean;
  /** K used in the K·G forms (SS ids 6/7, user option 3): the raw pass's K (out+0x24) — verified against the original. Defaults to the pass's own K. */
  kForG?: number;
  /** Resolved FC 4055 version (K bands). Defaults to PRE_SB343. */
  fc4055?: Fc4055Resolved;
}

/**
 * §2.2/§3 (FUN_10014624): CS and SS from a pair of nets; `g` are the raw formula nets for the K·G forms.
 * `ssNets` are the nets the SS formula (and its own K) works on when they differ from the CS nets: in the raw pass
 * the CS uses the formula nets DAT_10028314 while the SS uses the displayed nets + CS-only income, which the
 * CP-deduction allocation reduces by the custodial parent's add-on items (verified against the original).
 */
export function supportFromNets(nc: NCase, nets: [number, number], g: [number, number], opts: FormulaOptions = {}, ssNets: [number, number] = nets): SupportFromNets {
  const s = nc.c.settings;
  const hi: PartyIndex = nets[0] >= nets[1] ? 0 : 1;
  const lo = otherParty(hi);
  const H = hPercent(nc, hi);
  const hn = nets[hi], ln = nets[lo];
  const v = opts.fc4055 ?? 'PRE_SB343';
  const k = kFactor(hn + ln, H, nc.nkids, v);
  const kSs = ssNets === nets ? k : kFactor(ssNets[hi] + ssNets[lo], H, nc.nkids, v);
  let csHi: number;
  let cs: number;
  if (s.csMethod === 'USER') {
    const kidsWithUser = opts.tacticMode ? nc.children.filter((ch) => ch.userCs !== undefined) : [];
    cs = kidsWithUser.length
      ? kidsWithUser.reduce((a, ch) => a + signedBy(ch.userCs ?? 0, ch.userCsPayor ?? nc.c.userSupport.csPayor), 0)
      : signedBy(nc.c.userSupport.csAmount, nc.c.userSupport.csPayor);
    csHi = hi === 0 ? cs : -cs;
  } else {
    csHi = basicCs(k, hn, ln, H, nc.nkids);
    if (s.csMultiplier !== 1) csHi *= s.csMultiplier;
    cs = signedBy(csHi, hi);
  }
  let ss: number;
  if (s.ssMethod === 'USER') {
    ss = signedBy(nc.c.userSupport.ssAmount, nc.c.userSupport.ssPayor);
  } else {
    ss = signedBy(formulaSs(nc, { hn: ssNets[hi], ln: ssNets[lo], k: kSs, kG: opts.kForG ?? kSs, gHi: g[hi], gLo: g[lo], nkids: nc.nkids, csHi }), hi);
  }
  return { hi, hPercent: H, k, cs, ss, csHi };
}

// ------------------------------------------------------------------------------------------ §2.5 add-ons
/**
 * Net add-on child support (p0-signed) for the allocation method (FUN_10014757 / FUN_100145b1):
 *  - FC 4061(b) (FUN_1001604c): nets = unadjusted displayed nets + CS-only income; the SS moves from the higher
 *    earner to the lower, the CS is taken off whoever pays it (never added to the recipient); each party's share of
 *    the other's items = its net after support / the sum of the nets after support (floored at 0, no cap);
 *  - after support (FUN_10016183): nets = displayed (adjusted) nets, shares = net ∓ (CS + SS) over the sum of the
 *    nets BEFORE support, each capped at the other's items;
 *  - before support (FUN_100162aa): the same on the nets alone;
 *  - 50/50 (FUN_100163a5): half of the other's items each; CP deduction: 0 (already off the CP's net, §2.1).
 * `hi` is the higher-net party (out+8); `cs` / `ss` are p0-signed.
 */
export function allocateAddOns(nc: NCase, fc4061bNets: [number, number], displayedNets: [number, number], hi: PartyIndex, cs: number, ss: number, version: Fc4055Resolved = 'PRE_SB343'): number {
  const A: [number, number] = [nc.parties[0].addOnItems, nc.parties[1].addOnItems];
  if (A[0] === 0 && A[1] === 0) return 0;
  const lo = otherParty(hi);
  const csHi = hi === 0 ? cs : -cs;
  const ssHi = hi === 0 ? ss : -ss;
  const rel = (v: number) => (hi === 0 ? v : -v); // hi-relative → p0-signed
  const max0 = (v: number) => (v < 0 ? 0 : v);
  const split = (w: [number, number], total: number, cap: boolean): number => {
    if (Math.abs(total) <= 0.1) return 0;
    let shareHi = max0((max0(w[hi]) / total) * A[lo]);
    let shareLo = max0((max0(w[lo]) / total) * A[hi]);
    if (cap) { shareHi = Math.min(shareHi, A[lo]); shareLo = Math.min(shareLo, A[hi]); }
    return rel(shareHi - shareLo);
  };
  switch (effectiveCceAllocation(nc.c.settings.cceAllocation, version)) {
    case 'FIFTY_FIFTY': return (A[1] - A[0]) / 2; // party 0 pays half of party 1's items and vice versa
    case 'FC4061B': {
      const w: [number, number] = [0, 0];
      w[hi] = max0(fc4061bNets[hi] - ssHi);
      w[lo] = max0(fc4061bNets[lo] + ssHi);
      const payer = csHi >= 0.1 ? hi : lo;
      w[payer] = max0(w[payer] - Math.abs(csHi));
      return split(w, w[0] + w[1], false);
    }
    case 'AFTER_SUPPORT': {
      const sup = csHi + ssHi;
      const w: [number, number] = [0, 0];
      w[hi] = displayedNets[hi] - sup;
      w[lo] = displayedNets[lo] + sup;
      return split(w, displayedNets[0] + displayedNets[1], true);
    }
    case 'BEFORE_SUPPORT': return split(displayedNets, displayedNets[0] + displayedNets[1], true);
    case 'CP_DEDUCTION': return 0;
  }
}

// ------------------------------------------------------------------------------------------ §2.3 low income
/** FC 4055(b)(7) threshold for the tax year: the March figure of the year when CPI applies (and March has passed), else the prior one. */
export function lowIncomeThreshold(year: number, applyCpi: boolean, now: Date): number {
  const useCurrent = applyCpi && now.getMonth() + 1 >= 3;
  const key = useCurrent ? year : year - 1;
  return LOW_INCOME_THRESHOLD[key] ?? LOW_INCOME_THRESHOLD_FALLBACK;
}

/**
 * FC 4055(b)(7) as amended by SB 343: "the amount of monthly gross income earned from full-time minimum wage,
 * established by Section 1182.12 of the Labor Code, at 40 hours per week, 52 weeks per year" = wage × 2,080 / 12.
 * The wage is that of the case's tax year (CA_MINIMUM_WAGE); years outside the table use the nearest known year.
 * 2024: $2,773.33; 2025: $2,860.00; 2026: $2,929.33.
 */
export function sb343LowIncomeThreshold(year: number): number {
  const years = Object.keys(CA_MINIMUM_WAGE).map(Number).sort((a, b) => a - b);
  const y = Math.min(Math.max(year, years[0]), years[years.length - 1]);
  const wage = CA_MINIMUM_WAGE[y] ?? CA_MINIMUM_WAGE[years.filter((k) => k <= y).pop() as number];
  return (wage * SB343_FULL_TIME_HOURS_PER_YEAR) / 12;
}

/**
 * SB 343 low-income adjustment (FC 4055(b)(7), (c) as amended). When the obligor's net disposable income N is below
 * the threshold T there is a rebuttable presumption that the obligor is entitled to an adjustment, which reduces CS by
 * no more than CS × (T − N) / T; the presumption is rebutted only by evidence that "the lowest amount of child support
 * permitted" is unjust — so the presumed figure is the low end CS × N / T, and the range [low, CS] is always returned
 * (FC 4055(c): a computer program "shall provide the range"), independent of displayLowIncomeRange. The low end is
 * applied to the presumed CS in guideline mode (csMethod GUIDELINE) without the fc4055Checking switch, because the
 * statute presumes it; a court that finds the presumption rebutted picks a figure in the range (USER CS).
 */
export function lowIncomeAdjustmentSb343(nc: NCase, payorNet: number, cs: number, threshold = sb343LowIncomeThreshold(nc.year)): LowIncomeRange {
  const T = threshold;
  const inRange = payorNet > 0 && payorNet < T;
  const low = inRange ? cs - cs * (T - payorNet) / T : cs;
  const applied = inRange && nc.c.settings.csMethod === 'GUIDELINE';
  return { threshold: T, payorNet, low, high: cs, applied };
}

export function lowIncomeAdjustment(nc: NCase, payorNet: number, cs: number, now: Date, version: Fc4055Resolved = 'PRE_SB343'): LowIncomeRange {
  if (version === 'SB343') return lowIncomeAdjustmentSb343(nc, payorNet, cs);
  const s = nc.c.settings;
  const T = lowIncomeThreshold(nc.year, s.applyCpiToLowIncomeThreshold, now);
  const inRange = s.displayLowIncomeRange && payorNet > 0 && payorNet < T;
  const low = inRange ? cs * payorNet / T : cs;
  const applied = inRange && s.csMethod === 'GUIDELINE' && s.fc4055Checking;
  return { threshold: T, payorNet, low, high: cs, applied };
}

// ------------------------------------------------------------------------------------------ guideline pass
export interface GuidelineOptions extends FormulaOptions {
  releases: number;
  releaseFrom: PartyIndex;
  /** Optional per-child release split (ProposedSettings.releaseChildren); see ScenarioSpec.releaseKids. */
  releaseKids?: { ctc: number; odc: number };
  now: Date;
  /**
   * FC 4071(b) amounts to use as-is instead of iterating (FUN_10014ef4 only re-solves the hardship in guideline mode;
   * the tactic-9 rerun keeps the values the guideline pass wrote into the case).
   */
  fixedHardship?: [number, number];
}

export interface GuidelineOutcome {
  nets: [PartyNets, PartyNets];
  /** Adjusted nets (out+0x28), excluding CS-only income. */
  adjustedNets: [number, number];
  hi: PartyIndex;
  hPercent: number;
  k: number;
  kAdjusted: number;
  /** Resolved FC 4055 version and the add-on allocation actually used. */
  fc4055: Fc4055Resolved;
  cceAllocation: CceAllocation;
  /** p0-signed raw / adjusted supports. */
  rawCs: number; rawSs: number; adjCs: number; adjSs: number; bump: number;
  /** p0-signed outputs: presumed CS (after low-income), SS (incl. bump when on), add-ons, total. */
  presumedCs: number; ss: number; addOns: number; total: number;
  /** out+2: the CS payor (higher earner when CS is zero). */
  csPayor: PartyIndex;
  lowIncome: LowIncomeRange;
  hardshipCalc: [number, number];
  noSupportTaxes: [TaxOutcome, TaxOutcome];
  /** Taxes in the guideline scenario (with the output SS). */
  taxes: [TaxOutcome, TaxOutcome];
  scenario: ScenarioSpec;
  adjustedIterations: number;
  converged: boolean;
}

const spec = (o: GuidelineOptions, ss: number): ScenarioSpec => ({
  ss, releases: o.releases, releaseFrom: o.releaseFrom, zeroDependents: null, ...(o.releaseKids ? { releaseKids: o.releaseKids } : {}),
});

/** One guideline pass for fixed FC 4071(b) hardship amounts (FUN_10014a84 + FUN_10014d73). */
export function guidelinePass(ctx: TaxContext, o: GuidelineOptions, hardshipCalc: [number, number]): GuidelineOutcome {
  const nc = ctx.nc;
  const s = nc.c.settings;
  const fc4055 = resolveFc4055Version(nc.c, o.now);
  o = fc4055 === 'PRE_SB343' ? o : { ...o, fc4055 };
  const t0 = ctx.scenario(o.releases > 0 ? spec(o, 0) : NO_SUPPORT);
  const nets: [PartyNets, PartyNets] = [netDisposable(nc, 0, t0[0], hardshipCalc[0]), netDisposable(nc, 1, t0[1], hardshipCalc[1])];
  const formulaNets: [number, number] = [nets[0].formulaNet, nets[1].formulaNet];
  const disposable: [number, number] = [nets[0].netDisposable, nets[1].netDisposable];
  const csOnly: [number, number] = [nets[0].csOnly, nets[1].csOnly];

  // Raw supports on the raw nets (§2.2 / §3): CS on the formula nets, SS on the displayed nets + CS-only income.
  const raw = supportFromNets(nc, formulaNets, formulaNets, o, [disposable[0] + csOnly[0], disposable[1] + csOnly[1]]);
  const hi = raw.hi, lo = otherParty(hi);
  const remarried = (p: PartyIndex) => nc.parties[p].status === 'MFJOUT' || nc.parties[p].status === 'MFSOUT';
  // FUN_10014d73: a raw SS whose recipient (by sign) has remarried is zeroed.
  let rawSs = raw.ss;
  if (Math.abs(rawSs) >= 0.1 && remarried(otherParty(payorOf(rawSs) as PartyIndex))) rawSs = 0;
  // "Bumped" SS: case+0x1b84 is the single "method to adjust nets" code (NONE 0 / ACTUAL_CS 1 / ...); the engine
  // only tests == 1, and `bumpSs` forces 1 when the method gives 0 (bridge convention).
  const bumpOn = s.netAdjustMethod === 'ACTUAL_CS' || (s.bumpSs && s.netAdjustMethod === 'NONE');

  // §2.6 adjusted nets (FUN_100147dc): nets after the tax consequences of the guideline SS. No iteration when the
  // lower earner has remarried (adjusted SS = 0) or the parties file MFJIN (adjusted = raw).
  const adj: [number, number] = [disposable[0], disposable[1]];
  let adjSupport = raw;
  let adjSs = rawSs;
  let bump = 0;
  let iterations = 0;
  let converged = true;
  let taxes = t0;
  const hiRel = (p0Signed: number) => (hi === 0 ? p0Signed : -p0Signed); // amount relative to the higher earner
  if (remarried(lo)) {
    adjSs = 0;
  } else if (nc.parties[0].status !== 'MFJIN') {
    converged = false;
    // Bump bookkeeping (share of the post-CS pool the payor keeps at the raw nets; K, nets and SS relative to hi).
    const tnRaw = disposable[0] + disposable[1];
    let shareHi = 0, knHi = 0, base = 0;
    if (bumpOn && tnRaw > 0.1) {
      knHi = raw.k * disposable[hi];
      base = raw.k * tnRaw;
      shareHi = (disposable[hi] - (hiRel(rawSs) + knHi)) / (tnRaw - base);
    }
    let prevTax: [number, number] = [t0[0].incomeTax, t0[1].incomeTax];
    for (let i = 0; i < ADJUSTED_NETS_MAX_ITER; i++) {
      iterations = i + 1;
      const fn: [number, number] = [adj[0] + csOnly[0], adj[1] + csOnly[1]];
      adjSupport = supportFromNets(nc, fn, formulaNets, { ...o, kForG: raw.k });
      adjSs = adjSupport.ss;
      if (s.ssMethod === 'USER') { adjSs = rawSs; bump = 0; }
      const ssForTax = adjSs + (bumpOn ? signedBy(bump, hi) : 0);
      taxes = ctx.scenario(spec(o, ssForTax));
      let maxDelta = 0;
      for (const p of [0, 1] as const) {
        const delta = prevTax[p] - taxes[p].incomeTax;
        maxDelta = Math.max(maxDelta, Math.abs(delta));
        adj[p] += delta;
      }
      prevTax = [taxes[0].incomeTax, taxes[1].incomeTax];
      if (bumpOn) {
        const tn = adj[0] + adj[1];
        bump += adj[hi] - ((tn - base) * shareHi + knHi + bump + hiRel(adjSs));
        if (s.ssMethod === 'USER') bump = 0;
      }
      if (maxDelta < ADJUSTED_NETS_TOLERANCE) { converged = true; break; }
    }
  }

  // Outputs (out+0x4c / out+0x50): raw or adjusted per settings.
  const presumedRaw = s.csOnAdjustedNets ? adjSupport.cs : raw.cs;
  const ssOut = s.ssOnAdjustedNets ? adjSs + (bumpOn ? signedBy(bump, hi) : 0) : rawSs;
  const hiOut = s.csOnAdjustedNets ? adjSupport.hi : raw.hi;
  const csPayor: PartyIndex = payorOf(presumedRaw) ?? hiOut;

  // §2.3 low-income adjustment on the CS payor's net.
  const netsForCs = s.csOnAdjustedNets ? adj : disposable;
  const payorNet = netsForCs[csPayor] + csOnly[csPayor];
  const lowIncome = lowIncomeAdjustment(nc, payorNet, Math.abs(presumedRaw), o.now, fc4055);
  // FUN_100151fb applies the FC 4055 reduction only in guideline mode; the tactic-9 rerun (FUN_100152cc) skips it.
  const presumedCs = lowIncome.applied && !o.tacticMode ? signedBy(lowIncome.low, csPayor) : presumedRaw;

  // §2.5 add-ons: FC 4061(b) on the unadjusted displayed nets + CS-only income; the other methods on the displayed
  // (adjusted, case+0x1b82 = 1) nets. The higher-net party is out+8.
  const addOns = allocateAddOns(nc, [disposable[0] + csOnly[0], disposable[1] + csOnly[1]], adj, hi, presumedCs, ssOut, fc4055);

  // Taxes for the guideline scenario (a memo hit unless the raw SS was chosen).
  const gTaxes = ctx.scenario(spec(o, ssOut));

  return {
    nets, adjustedNets: adj, hi: hiOut, hPercent: s.csOnAdjustedNets ? adjSupport.hPercent : raw.hPercent, k: raw.k, kAdjusted: adjSupport.k,
    fc4055, cceAllocation: effectiveCceAllocation(s.cceAllocation, fc4055),
    rawCs: raw.cs, rawSs, adjCs: adjSupport.cs, adjSs, bump,
    presumedCs, ss: ssOut, addOns, total: presumedCs + ssOut + addOns, csPayor, lowIncome,
    hardshipCalc, noSupportTaxes: t0, taxes: gTaxes, scenario: spec(o, ssOut), adjustedIterations: iterations, converged,
  };
}

/** §2.4 FC 4071(b) hardship for party p from the current guideline outcome. */
function hardshipFor(nc: NCase, p: PartyIndex, g: GuidelineOutcome): number {
  const np = nc.parties[p];
  const nOther = Math.min(10, Math.max(0, Math.round(np.in.hardship.otherSupportedChildren)));
  if (!np.in.hardship.calculate || nOther <= 0 || nc.nkids <= 0) return 0;
  const s = nc.c.settings;
  let base: number;
  switch (s.hardshipLimitBase) {
    case 'CS_ALLOCATION': base = (g.nets[0].formulaNet + g.nets[1].formulaNet) * g.k; break;
    case 'BASIC_CS': base = Math.abs(g.presumedCs); break;
    default: base = Math.abs(g.presumedCs + g.addOns);
  }
  const mult = s.hardshipLimitMultiplier[p];
  return mult * base * 0.01 / nc.nkids * nOther;
}

/** Full guideline computation: the hardship fixed-point loop around guidelinePass (FUN_10014ef4). */
export function computeGuideline(ctx: TaxContext, o: GuidelineOptions): GuidelineOutcome {
  const nc = ctx.nc;
  if (o.fixedHardship) return guidelinePass(ctx, o, o.fixedHardship);
  let hardship: [number, number] = [0, 0];
  const needs = [0, 1].some((p) => nc.parties[p as PartyIndex].in.hardship.calculate && nc.parties[p as PartyIndex].in.hardship.otherSupportedChildren > 0) && nc.nkids > 0;
  let g = guidelinePass(ctx, o, hardship);
  if (!needs) return g;
  for (let i = 0; i < HARDSHIP_MAX_ITER; i++) {
    const next: [number, number] = [hardshipFor(nc, 0, g), hardshipFor(nc, 1, g)];
    const done = Math.abs(next[0] - hardship[0]) < HARDSHIP_TOLERANCE && Math.abs(next[1] - hardship[1]) < HARDSHIP_TOLERANCE;
    if (done) { hardship = next; g = guidelinePass(ctx, o, hardship); break; }
    hardship = [(hardship[0] + next[0]) / 2, (hardship[1] + next[1]) / 2];
    g = guidelinePass(ctx, o, hardship);
  }
  return g;
}
